"use client";

import clsx from "clsx";
import { createContext, useContext, useState } from "react";

/**
 * Ilot client minimal de la liste de services.
 *
 * Seul l'index survole traverse la frontiere serveur/client : les cartes et les
 * visuels sont rendus par le serveur et passes en `children`. Le convertisseur
 * RichText de Payload et la resolution des `Media` restent donc cote serveur,
 * et le JS embarque se limite au contexte et aux deux enveloppes ci-dessous.
 */
const HoveredFeatureContext = createContext(0);
const SelectFeatureContext = createContext<(index: number) => void>(() => {});

type SlotProps = {
  index: number;
  className?: string;
  /** Classes appliquees quand la carte est celle dont le visuel est affiche. */
  activeClassName?: string;
  inactiveClassName?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
};

export function FeatureHoverGroup({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  const [hovered, setHovered] = useState(0);

  return (
    <SelectFeatureContext.Provider value={setHovered}>
      <HoveredFeatureContext.Provider value={hovered}>
        <div className={className}>{children}</div>
      </HoveredFeatureContext.Provider>
    </SelectFeatureContext.Provider>
  );
}

/**
 * `onFocus` autant que `onMouseEnter` : la navigation au clavier fait defiler
 * les visuels comme la souris, ce que les onglets Headless UI precedents ne
 * faisaient pas (leur index interne et l'etat du panneau divergeaient).
 */
export function FeatureCard({
  index,
  className,
  activeClassName,
  inactiveClassName,
  style,
  children,
}: SlotProps) {
  const hovered = useContext(HoveredFeatureContext);
  const select = useContext(SelectFeatureContext);
  const isActive = hovered === index;

  return (
    <div
      className={clsx(className, isActive ? activeClassName : inactiveClassName)}
      onMouseEnter={() => select(index)}
      onFocus={() => select(index)}
      style={style}
    >
      {children}
    </div>
  );
}

export function FeaturePreview({
  index,
  className,
  activeClassName,
  inactiveClassName,
  style,
  children,
}: SlotProps) {
  const hovered = useContext(HoveredFeatureContext);
  const isActive = hovered === index;

  return (
    <div
      className={clsx(className, isActive ? activeClassName : inactiveClassName)}
      style={style}
    >
      {children}
    </div>
  );
}
